import React, { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import style from "../Application.module.scss";
import pageHeadingStyles from "../../../styles/PageHeader.module.scss";
import ButtonIcon from "../../../components/ButtonIcon/ButtonIcon";
import SearchInput from "../../../components/SearchInput/SearchInput";
import ApplicationListing from "./ApplicationListing";
import FilterDrawer from "./FIlter/FilterDrawer/FilterDrawer";
import Filter from "../../../assets/svg/icons/common/Filter";
import { getPreapp } from "../../../../api";
import useCommonStore from "../../../stores/common";
import useApplicationStore from "../../../stores/application";
import useDomainStore from "../../../stores/domain";
import RedDotBadge from "../../../assets/svg/icons/common/RedDotBadge";
import PlusIcon16 from "../../../assets/svg/icons/common/PlusIcon16";

function getApplicationFilterCount(query) {
  if (!query) return 0;
  const orgId =
    typeof query.organization_id === "object" &&
    query.organization_id?.value != null
      ? query.organization_id.value
      : query.organization_id;
  const hasDateRange = query.created_from !== "" || query.created_to !== "";
  return (
    (hasDateRange ? 1 : 0) +
    (query.progression_id?.length ?? 0) +
    (query.rep_codes?.length ?? 0) +
    (query.manager_id?.length ?? 0) +
    (query.uw_ids?.length ?? 0) +
    (orgId ? 1 : 0) +
    (query.user_id?.length ?? 0) +
    (query.bank?.length ?? 0) +
    (query.left_progression_filter?.length ?? 0) +
    (query.channel != null && query.channel !== "" ? 1 : 0) +
    (query.ems_office?.length ?? 0) +
    (query.ems_branch?.length ?? 0)
  );
}

function Application() {
  const { isKurvDomain } = useDomainStore();
  const setFilterDrawerOpen = useCommonStore(
    (state) => state.setFilterDrawerOpen,
  );
  const appFilterSearchQuery = useApplicationStore(
    (state) => state.appFilterSearchQuery,
  );
  const setAppFilterSearchQuery = useApplicationStore(
    (state) => state.setAppFilterSearchQuery,
  );
  const setPreappsLoading = useApplicationStore(
    (state) => state.setPreappsLoading,
  );
  const setPageCount = useApplicationStore((state) => state.setPageCount);
  const setPreapps = useApplicationStore((state) => state.setPreapps);
  const setTotalItems = useApplicationStore((state) => state.setTotalItems);
  const setItemsPerPage = useApplicationStore((state) => state.setItemsPerPage);
  const setRelationshipManagers = useApplicationStore(
    (state) => state.setRelationshipManagers,
  );

  const [searchText, setSearchText] = useState("");
  const [filterApplied, setFilterApplied] = useState(false);
  const cancelRef = useRef(null);
  const ignoreRef = useRef(false);
  const debounceTimeoutRef = useRef(null);

  const onChangeSearchInput = (e) => {
    const value = e.target.value;
    setSearchText(value);

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      setAppFilterSearchQuery({
        search: value,
        page: 1,
      });
    }, 500);
  };

  const loadPreapps = useCallback(() => {
    if (cancelRef.current) {
      console.log("before cancel", appFilterSearchQuery.search);
      cancelRef.current.cancel();
    }

    cancelRef.current = axios.CancelToken.source();
    ignoreRef.current = false;

    setPreappsLoading(true);

    getPreapp(appFilterSearchQuery, cancelRef.current.token)
      .then((res) => {
        if (ignoreRef.current) {
          return;
        }

        if (res.status) {
          setRelationshipManagers(res?.relation_manager);
          setTotalItems(res?.total_count);
          setPreapps(res?.data?.data ?? []);
          setPageCount(res?.data?.last_page);
          setItemsPerPage(50);
        } else {
          setPreapps([]);
        }
      })
      .catch((error) => {
        if (!axios.isCancel(error)) {
          console.error(error);
          console.log("Request cancelled");
          if (!ignoreRef.current) {
            setPreapps([]);
          }
        }
      })
      .finally(() => {
        if (!ignoreRef.current) {
          setPreappsLoading(false);
        }
      });
  }, [
    appFilterSearchQuery,
    setPreappsLoading,
    setRelationshipManagers,
    setTotalItems,
    setPreapps,
    setPageCount,
    setItemsPerPage,
  ]);

  useEffect(() => {
    loadPreapps();
    setFilterApplied(getApplicationFilterCount(appFilterSearchQuery) > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    appFilterSearchQuery.created_from,
    appFilterSearchQuery.created_to,
    appFilterSearchQuery.search,
    appFilterSearchQuery.progression_id,
    appFilterSearchQuery.rep_codes,
    appFilterSearchQuery.manager_id,
    appFilterSearchQuery.uw_ids,
    appFilterSearchQuery.organization_id,
    appFilterSearchQuery.user_id,
    appFilterSearchQuery.bank,
    appFilterSearchQuery.left_progression_filter,
    appFilterSearchQuery.channel,
    appFilterSearchQuery.ems_office,
    appFilterSearchQuery.ems_branch,
    loadPreapps,
  ]);

  useEffect(() => {
    return () => {
      ignoreRef.current = true;
      if (cancelRef.current) {
        cancelRef.current.cancel();
      }
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  return (
    <>
      <div className={style.mobile_header}>
        <div className={style.mobile_filters}>
          <div className={style.search_wrapper}>
            <SearchInput onChange={onChangeSearchInput} value={searchText} />
          </div>
          <ButtonIcon
            bgColor="#EDEDF4"
            onClick={() => setFilterDrawerOpen(true)}
            className={style.filter_button}
          >
            <Filter />
            {filterApplied && <RedDotBadge className={style.filter_active} />}
          </ButtonIcon>
          <Link to="/create-app/usa" className={style.create_button_mobile}>
            <ButtonIcon bgColor="#4C70EF" className={style.create_button}>
              <PlusIcon16 />
            </ButtonIcon>
          </Link>
        </div>
      </div>

      <div
        className={`${pageHeadingStyles.page_heading} ${pageHeadingStyles.column}`}
      >
        <div className={pageHeadingStyles.title_contents}>
          <p>All Applications</p>
        </div>
        {/* <div className={style.right_side}></div> */}

        <div className={style.search_input_container}>
          <SearchInput onChange={onChangeSearchInput} value={searchText} />

          <ButtonIcon
            bgColor="#EDEDF4"
            onClick={() => setFilterDrawerOpen(true)}
            className={style.filter_button}
          >
            <Filter /> Filter
            {filterApplied &&
            getApplicationFilterCount(appFilterSearchQuery) > 0
              ? `: ${getApplicationFilterCount(appFilterSearchQuery)}`
              : ""}
            {filterApplied && <RedDotBadge />}
          </ButtonIcon>
        </div>
      </div>

      <div className={`${style.card} ${isKurvDomain ? style.kurv_theme : ""}`}>
        <ApplicationListing fetchList={loadPreapps} />
      </div>

      <FilterDrawer />
    </>
  );
}

export default Application;
